import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiErrorCode, ApiFailure, ApiSuccess, TokenPair } from '@todo/shared';
import { clearTokens, getAccessToken, getRefreshToken, storeTokens } from './tokens';

/**
 * The single HTTP client for the whole app.
 *
 * Two interceptors do all the tedious work exactly once, so no component ever
 * writes an Authorization header or handles a 401:
 *
 *   request  -> attach the access token
 *   response -> on 401, refresh once and replay the original request
 */

/** In dev this is '' so requests hit /api on the same origin and Vite proxies
 *  them to :4000. In production set VITE_API_URL to the deployed API. */
const baseURL = `${import.meta.env.VITE_API_URL ?? ''}/api`;

export const api: AxiosInstance = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 20_000,
});

/**
 * An API error the UI can actually branch on, rather than a raw AxiosError.
 * `code` comes straight from the shared ApiErrorCode union.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details?: unknown;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// ---------------------------------------------------------------------------
// Request interceptor: attach the token
// ---------------------------------------------------------------------------

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor: refresh once on 401, then replay
// ---------------------------------------------------------------------------

/** Marks a request we have already retried, so a failure cannot loop forever. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/**
 * The refresh currently in flight, if any.
 *
 * Without this, five components rendering at once would each see a 401 and
 * fire five refresh calls. The first caller starts the refresh; everyone else
 * awaits the SAME promise and then replays their own request.
 */
let refreshInFlight: Promise<TokenPair> | null = null;

/** Notified when refresh fails, so AuthContext can drop the session. */
type LogoutListener = () => void;
let onSessionExpired: LogoutListener | null = null;

export function setSessionExpiredHandler(handler: LogoutListener | null): void {
  onSessionExpired = handler;
}

async function refreshTokens(): Promise<TokenPair> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new ApiError(401, 'UNAUTHORIZED', 'No refresh token available');
  }

  /**
   * A BARE axios call, not `api`.
   *
   * Using the configured instance would run the interceptors again: it would
   * attach the expired access token, and a 401 on the refresh call itself
   * would recurse straight back into this function.
   */
  const response = await axios.post<ApiSuccess<TokenPair>>(
    `${baseURL}/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const tokens = response.data.data;
  storeTokens(tokens);
  return tokens;
}

api.interceptors.response.use(
  (response) => response,

  async (error: AxiosError<ApiFailure>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    const shouldRefresh =
      status === 401 &&
      config !== undefined &&
      !config._retried &&
      // Never try to refresh a failed login or a failed refresh.
      !config.url?.includes('/auth/login') &&
      !config.url?.includes('/auth/refresh');

    if (shouldRefresh) {
      config._retried = true;

      try {
        refreshInFlight ??= refreshTokens();
        await refreshInFlight;
        return await api.request(config);
      } catch {
        clearTokens();
        onSessionExpired?.();
        throw new ApiError(401, 'UNAUTHORIZED', 'Your session has expired');
      } finally {
        refreshInFlight = null;
      }
    }

    throw toApiError(error);
  },
);

/**
 * The per-field messages a 422 carries, flattened into one readable line.
 *
 * The API's validate middleware sends `message: 'Request validation failed'`
 * - true, and useless to the person looking at the form - with the real
 * reasons in `details`, keyed by target and path:
 *
 *   { "body.assigneeIds": ["Assign the task to at least one person"] }
 *
 * Every form in the app renders `err.message`, so showing the person
 * something they can act on means lifting those strings into the message
 * HERE, once, rather than teaching each form to dig through `details`.
 * `details` is left intact for any form that wants to mark the field.
 *
 * Returns null if the shape is not what we expect, so an odd payload falls
 * back to the API's own message rather than to an empty string.
 */
function detailMessage(details: unknown): string | null {
  if (typeof details !== 'object' || details === null) return null;

  const lines: string[] = [];

  for (const value of Object.values(details as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      for (const message of value) {
        if (typeof message === 'string' && message.trim() !== '') {
          lines.push(message.trim());
        }
      }
    } else if (typeof value === 'string' && value.trim() !== '') {
      lines.push(value.trim());
    }
  }

  // De-duplicate: the same rule can fail on two fields at once.
  const unique = [...new Set(lines)];
  return unique.length > 0 ? unique.join('\n') : null;
}

/** AxiosError -> ApiError, using the envelope the API always sends. */
function toApiError(error: AxiosError<ApiFailure>): ApiError {
  const body = error.response?.data;

  if (body?.error) {
    const message =
      body.error.code === 'VALIDATION_ERROR'
        ? detailMessage(body.error.details) ?? body.error.message
        : body.error.message;

    return new ApiError(
      error.response?.status ?? 500,
      body.error.code,
      message,
      body.error.details,
    );
  }

  if (error.code === 'ECONNABORTED') {
    return new ApiError(408, 'INTERNAL_ERROR', 'The request timed out');
  }

  if (!error.response) {
    return new ApiError(
      0,
      'INTERNAL_ERROR',
      'Cannot reach the server. Check your connection.',
    );
  }

  return new ApiError(
    error.response.status,
    'INTERNAL_ERROR',
    'Something went wrong',
  );
}

// ---------------------------------------------------------------------------
// In-flight GET de-duplication
// ---------------------------------------------------------------------------

/**
 * Identical GETs issued while the first is still on the wire share ONE
 * network request. Two situations produce such duplicates:
 *
 *   - React StrictMode's dev-only double effect run, which fired every
 *     mount fetch twice in development;
 *   - two widgets that legitimately need the same data at the same moment
 *     (e.g. the bell and a toast group both asking for unread items).
 *
 * This is NOT a cache: the entry lives only while the request is in flight
 * and is removed the moment it settles, so a refetch after a mutation
 * always hits the server for fresh data.
 */
const inFlightGets = new Map<string, Promise<AxiosResponse<unknown>>>();

function dedupedGet<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<AxiosResponse<ApiSuccess<T>>> {
  const key = `${url}?${JSON.stringify(params ?? {})}`;
  const existing = inFlightGets.get(key);
  if (existing) return existing as Promise<AxiosResponse<ApiSuccess<T>>>;

  const request = api
    .get<ApiSuccess<T>>(url, { params })
    .finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, request as Promise<AxiosResponse<unknown>>);
  return request;
}

// ---------------------------------------------------------------------------
// Thin helpers that unwrap the envelope
// ---------------------------------------------------------------------------

/**
 * Every endpoint wrapper uses these, so no component ever writes
 * `response.data.data` and wonders which `data` is which.
 */
export async function get<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<T> {
  const response = await dedupedGet<T>(url, params);
  return response.data.data;
}

/** Returns the envelope intact when you need `meta` for pagination. */
export async function getPaged<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<ApiSuccess<T>> {
  const response = await dedupedGet<T>(url, params);
  return response.data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const response = await api.post<ApiSuccess<T>>(url, body);
  return response.data.data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const response = await api.patch<ApiSuccess<T>>(url, body);
  return response.data.data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const response = await api.put<ApiSuccess<T>>(url, body);
  return response.data.data;
}

export async function del(url: string): Promise<void> {
  await api.delete(url);
}
